// packages/0-foundation/core.module/src/v1/browser.ts

import { createConsola } from "consola/browser";
import { ConsolaLoggerBase } from "./_universal/lvl_1/logger.part/classes/consola-logger-base.class";
import type { ILogger } from "./_universal/lvl_1/logger.part/types/log.type";
import type { CrashSink } from "./_universal/lvl_1/utils/crash-reporter.fn";
import {
  resolveDebugFlagLevel,
  type IDebugFlag,
} from "./_universal/lvl_1/utils/debug-flag.fn";

export { ConsolaLoggerBase } from "./_universal/lvl_1/logger.part/classes/consola-logger-base.class";
export type {
  ILogger,
  LogRecordType,
} from "./_universal/lvl_1/logger.part/types/log.type";

// Transport-free primitives, safe for browser bundles.
export { AdapterError } from "./_universal/lvl_1/errors/adapter-error.class";
export { AppError } from "./_universal/lvl_1/errors/app-error.class";
export { isUuid } from "./_universal/lvl_1/utils/is-uuid.fn";
export { isUniqueViolationError } from "./_universal/lvl_1/utils/unique-violation.fn";
export { isRecord } from "./_universal/lvl_1/utils/is-record.fn";
export { signHmacSha256, timingSafeEqualString, HMAC_SHA256_KEY_MIN_LENGTH } from "./_universal/lvl_1/utils/hmac.fn";
export {
  signCompactToken,
  verifyCompactToken,
  COMPACT_TOKEN_KEY_MIN_LENGTH,
} from "./_universal/lvl_1/utils/compact-token.fn";
export type {
  CompactTokenPayload,
  CompactTokenVerifyResult,
} from "./_universal/lvl_1/utils/compact-token.fn";
export { createCrashReporter } from "./_universal/lvl_1/utils/crash-reporter.fn";
export type {
  CrashContext,
  CrashReporter,
  CrashSink,
} from "./_universal/lvl_1/utils/crash-reporter.fn";
export {
  DEBUG_FLAG_LOG_LEVEL,
  isDebugFlagEnabled,
  resolveDebugFlagLevel,
} from "./_universal/lvl_1/utils/debug-flag.fn";
export type { IDebugFlag } from "./_universal/lvl_1/utils/debug-flag.fn";
export {
  MS_PER_DAY,
  MS_PER_HOUR,
  MS_PER_MINUTE,
  MS_PER_SECOND,
} from "./_universal/lvl_1/utils/time.const";
export {
  createHttpTransport,
  HTTP_TRANSPORT_ERROR_KIND,
  HttpTransportError,
  isHttpTransportError,
} from "./_universal/lvl_3/utils/http-transport.fn";
export type {
  HttpErrorBodyDetails,
  HttpFetchImpl,
  HttpTransport,
  HttpTransportConfig,
  HttpTransportErrorKind,
  HttpTransportRequestOptions,
} from "./_universal/lvl_3/utils/http-transport.fn";
export { withKeyedLock } from "./_universal/lvl_3/utils/keyed-lock.fn";

export function createLogger(props: {
  agentName: string;
  level: number;
  omitKeys?: string[];
  /** Маскирует ключи под SENSITIVE_KEY_PATTERN (pass/token/secret/…) в data каждой записи. Opt-in, default false. */
  maskSensitive?: boolean;
  /** Репортер ошибок: вызывается из error()/fatal() после печати; извлекает Error из data или data.error, иначе синтезирует из message. */
  crashSink?: CrashSink;
  /**
   * Browser debug toggle (`?<param>=1` persists to localStorage, `=0` clears).
   * When enabled, the effective level is raised to DEBUG_FLAG_LOG_LEVEL.
   */
  debugFlag?: IDebugFlag;
}): ILogger {
  return new ConsolaLoggerBase({
    agentName: props.agentName,
    omitKeys: props.omitKeys ?? [],
    maskSensitive: props.maskSensitive ?? false,
    ...(props.crashSink ? { crashSink: props.crashSink } : {}),
    consola: createConsola({
      level: resolveDebugFlagLevel(props.level, props.debugFlag),
    }),
  });
}
